import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Send, MoreVertical, Info, Megaphone, Lock, Ban, Mic, Square, MapPin, ImageIcon, X as XIcon } from 'lucide-react';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { FlagImage } from './utils/countryFlags';
import RankBadge from './RankBadge';
import { ActivityStatus } from './ui/ActivityStatus';
import { sanitizeLocation } from './utils/rankUtils';
import { formatTimeAgo } from './utils/timeFormat';
import { useMessaging } from './MessagingContext';
import { useUserData } from './UserDataContext';
import { EmbeddedYap } from './EmbeddedYap';
import { getYapInteractionId, getOriginalYapId } from './utils/yapUtils';
import { ParallaxBackground } from './utils/parallax';
import { getDbSafe } from '../services/firebaseConfig';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { StorageService } from '../services/StorageService';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { GovernanceService } from '../services/GovernanceService';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useImageViewer } from './ui/ImageViewerProvider';

interface Message {
  id: string;
  sender: 'user' | 'friend';
  text: string;
  time: string;
  timestamp: number;
  audioUrl?: string;
  audioDuration?: number;
  imageUrl?: string;
  read?: boolean;
  edited?: boolean;
  isProvisional?: boolean;
  likes?: number;
  likedBy?: string[];
  replyTo?: { id: string; text: string; senderId: string; type?: 'text' | 'image' | 'voice' };
  sharedYap?: {
    id: number | string;
    author: string;
    username: string;
    avatar: string;
    content: string;
    time: string;
    likes: number;
    comments: number;
    shouts: number;
    image?: string;
    originalYap?: {
      id: string | number;
      author: string;
      username: string;
      content: string;
    };
  };
  groupInvite?: {
    id: string;
    name: string;
    type: 'tribe' | 'forum' | 'room';
    bannerUrl: string;
    isPublic?: boolean;
  };
}

interface DirectMessageScreenProps {
  friend: {
    id: number | string;
    name: string;
    username?: string;
    image: string;
    avatar?: string;
    country?: string;
    countryFlag?: string;
    location?: string;
    online?: boolean;
  };
  isRequest?: boolean;
  onBack: () => void;
  onChatCreated?: (chatData: any) => void;
}

/**
 * Mirrors RoomScreen: tells the server this chat is on screen so it does not
 * push about it. A native push is drawn by the OS and cannot be recalled, so
 * the send has to be skipped server-side, which means the server has to know.
 * Cleared on unmount and when the tab is hidden.
 */
function useActiveConversation(key: string | null) {
  useEffect(() => {
    let cancelled = false;
    const write = async (value: string | null) => {
      try {
        const { getDbSafe, getAuthSafe } = await import('../services/firebaseConfig');
        const { doc, updateDoc } = await import('firebase/firestore');
        const auth = await getAuthSafe();
        const uid = auth.currentUser?.uid;
        if (!uid || cancelled) return;
        const db = await getDbSafe();
        await updateDoc(doc(db, 'users', uid), { activeConversation: value || '' });
      } catch { /* presence is an optimisation, never a blocker */ }
    };

    if (key) write(key);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') write(null);
      else if (key) write(key);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      write(null);
    };
  }, [key]);
}

export function DirectMessageScreen({ friend, onBack, onChatCreated, isRequest = false }: DirectMessageScreenProps) {
  // Suppresses pushes for this chat while it is on screen.
  useActiveConversation(friend?.id ? `dm:${String(friend.id)}` : null);
  // 🔒 CRASH PROTECTION: Ensure friend exists before rendering
  console.log('[DirectMessageScreen] Rendering with friend:', friend.name, 'username:', friend.username);
  if (!friend || !friend.id) {
    console.warn('[DirectMessageScreen] Missing friend or friend ID. Redirecting back.');
    useEffect(() => {
      onBack();
    }, [onBack]);
    return <div className="w-full h-full bg-[#0D0D0D] flex items-center justify-center"><p className="text-white/50">Loading chat...</p></div>;
  }

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  useScreenReady(!isLoading);
  const [isSending, setIsSending] = useState(false); // 🔒 Prevents double-send race conditions
  const { getChat, addMessage, getChatMessages, enterChat, leaveChat, deleteChat, editMessage, deleteMessage, acceptRequest, declineRequest } = useMessaging();
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null); // ✏️ Edit state
  const { blockUser, isUserBlocked, firebaseUser, acceptGroupInvite, joinTribe, joinForum } = useUserData(); // Use context for blocking
  const [friendLocation, setFriendLocation] = useState<string>('');
  const [toggledTimes, setToggledTimes] = useState<Set<string>>(new Set());
  const [activeMessageMenu, setActiveMessageMenu] = useState<string | null>(null);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const inputBarRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(88);
  const [inputBarHeight, setInputBarHeight] = useState(80);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { open: openImage } = useImageViewer();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // ── Typing indicator state ──────────────────────────────────────────────────
  const [friendTyping, setFriendTyping] = useState(false);
  const lastTypingPing = useRef(0);
  const typingClearTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── Chat-message gestures ──────────────────────────────────────────────────
  // Double-tap a message bubble to like it; long-press to quote-reply.
  const lastTapRef = useRef<{ id: string; t: number }>({ id: '', t: 0 });
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const longPressFiredRef = useRef(false);
  const [replyingToMsg, setReplyingToMsg] = useState<Message | null>(null);
  // Optimistic like state — keyed by message id so the UI flips instantly
  // before Firestore confirms the write.
  const [optimisticLikes, setOptimisticLikes] = useState<Record<string, boolean>>({});

  const isMessageLiked = (msg: Message): boolean => {
    const uid = firebaseUser?.uid || '';
    if (msg.id in optimisticLikes) return optimisticLikes[msg.id];
    return (msg.likedBy || []).includes(uid);
  };

  const handleLikeMessage = async (msg: Message) => {
    if (!existingChat?.id || !firebaseUser) return;
    if (msg.isProvisional) return; // can't like a not-yet-sent message
    const liked = isMessageLiked(msg);
    setOptimisticLikes(prev => ({ ...prev, [msg.id]: !liked }));
    try {
      const { MessagingService } = await import('../services/api');
      await MessagingService.toggleMessageLike(existingChat.id, msg.id, firebaseUser.uid);
    } catch (e) {
      // Roll back optimistic state on failure.
      setOptimisticLikes(prev => ({ ...prev, [msg.id]: liked }));
      console.error('[DirectMessageScreen] toggleMessageLike failed:', e);
    }
  };

  // Pointer-down handler that arms BOTH gestures.
  const handleMessagePointerDown = (msg: Message) => {
    longPressFiredRef.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      // ~550ms hold → quote reply.
      longPressFiredRef.current = true;
      setReplyingToMsg(msg);
      // Subtle haptic on supported devices.
      try { (navigator as any)?.vibrate?.(15); } catch { /* ignore */ }
    }, 550);
  };

  const handleMessagePointerUp = (msg: Message) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = undefined;
    }
    if (longPressFiredRef.current) return;
    // Double-tap detection — second tap within 320ms on the same message.
    const now = Date.now();
    const prev = lastTapRef.current;
    if (prev.id === msg.id && now - prev.t < 320) {
      handleLikeMessage(msg);
      lastTapRef.current = { id: '', t: 0 };
    } else {
      lastTapRef.current = { id: msg.id, t: now };
    }
  };

  const handleMessagePointerCancel = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = undefined;
    }
  };

  // 📝 Auto-resize textarea logic
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 160); // Limit growth to ~6-7 lines
      textareaRef.current.style.height = `${newHeight}px`;
      
      // Update input bar height measurement to adjust message area bottom padding
      if (inputBarRef.current) setInputBarHeight(inputBarRef.current.offsetHeight);
    }
  }, [message]);

  const handleImagePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10 MB');
      return;
    }
    setPendingImage(file);
    setPendingImagePreview(URL.createObjectURL(file));
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const clearPendingImage = () => {
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    setPendingImage(null);
    setPendingImagePreview(null);
  };

  // 🎙️ Voice Recording State
  const { 
    isRecording, 
    isPaused,      // ⏸️ Added
    duration, 
    startRecording, 
    stopRecording, 
    pauseRecording,   // ⏸️ Added
    resumeRecording,  // ⏸️ Added
    audioBlob, 
    reset: resetRecorder, 
    error: recorderError 
  } = useVoiceRecorder(60000); // 🔒 DM Limit: 1 Minute

  // Get existing chat or create new one
  const existingChat = getChat(friend.id);
  const messages = getChatMessages(friend.id);

  // 🔒 REQUEST LOGIC: Determine if current user is the sender or recipient of the request
  const isActuallyRequest = isRequest && (!existingChat || (existingChat as any).isRequest !== false);
  const isSenderOfRequest = isActuallyRequest && existingChat && (existingChat as any).requestSenderId === firebaseUser?.uid;
  const isRecipientOfRequest = isActuallyRequest && existingChat && (existingChat as any).requestSenderId !== firebaseUser?.uid;
  // Count messages sent by current user in this request chat
  const myMessageCount = messages.filter(m => m.sender === 'user').length;
  const isInputLockedByRequest = isSenderOfRequest && myMessageCount >= 1;

  // [Removed: was logging friend.name — PII leak]

  // 🔒 Measure header and input bar heights so messages area fills exactly the space between them
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
      if (inputBarRef.current) setInputBarHeight(inputBarRef.current.offsetHeight);
    };
    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [isLoading]); // Re-measure after loading finishes (input bar may change)

  // 🔒 Track whether this is the very first load (snap instantly, then smooth after)
  const isInitialLoad = useRef(true);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return; // Wait until initial fetch is complete

    if (isInitialLoad.current) {
      // Instant snap to bottom on first load with loaded messages — no animation
      container.scrollTop = container.scrollHeight;
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      isInitialLoad.current = false;
    } else {
      // Smooth scroll for new incoming/outgoing messages
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    // 🔒 PATCH: Mark messages as read when viewing screen
    if (messages.length > 0 && existingChat && firebaseUser) {
      import('../services/api').then(({ MessagingService }) => {
        MessagingService.markMessagesAsRead(existingChat.id, firebaseUser.uid).catch(console.error);
      });
    }
  }, [messages, existingChat?.id, firebaseUser, isLoading]);

  // Subscribe to chat messages
  useEffect(() => {
    let chatId: string;
    
    // 🔒 FIX: Compute deterministic ID for cleanup safety
    // This ensures leaveChat works even if enterChat promise hasn't resolved yet
    // or if the component unmounts during the async operation.
    if (firebaseUser?.uid && friend.id) {
       const key1 = String(firebaseUser.uid);
       const key2 = String(friend.id);
       chatId = [key1, key2].sort().join('_');
    }

    const initChat = async () => {
      try {
        await enterChat(String(friend.id));
      } catch (error) {
        console.error("[DirectMessageScreen] Error entering chat with friend", friend.id, ":", error);
      } finally {
        setIsLoading(false);
      }
    };

    initChat();

    return () => {
      if (chatId) leaveChat(chatId);
    };
  }, [friend.id, firebaseUser]);

  // The friend's activity status is rendered by <ActivityStatus live /> in the
  // header. The subscription that used to sit here stored an exact last-seen
  // clock time that was never displayed, and status/{uid} is no longer publicly
  // readable, so it would now just log permission errors.

  // Subscribe to chat doc to read the friend's typing timestamp.
  useEffect(() => {
    if (!existingChat?.id) return;
    let unsub: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      const db = await getDbSafe();
      unsub = onSnapshot(doc(db, 'chats', existingChat.id), (snap) => {
        const data = snap.data() as any;
        const friendUid = String(friend.id);
        const t = data?.typing?.[friendUid];
        const ts = t?.toMillis?.() ?? 0;
        const fresh = ts > 0 && Date.now() - ts < 5000;
        setFriendTyping(fresh);
        if (fresh) {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => setFriendTyping(false), 5000 - (Date.now() - ts));
        }
      });
    })();
    return () => {
      if (unsub) unsub();
      if (timer) clearTimeout(timer);
    };
  }, [existingChat?.id, friend.id]);

  // Throttled write of "I'm typing" to the chat doc.
  // Fires at most once every 2s; cleared on send.
  const pingTyping = async () => {
    if (!existingChat?.id || !firebaseUser) return;
    const now = Date.now();
    if (now - lastTypingPing.current < 2000) return;
    lastTypingPing.current = now;
    try {
      const db = await getDbSafe();
      const { updateDoc, serverTimestamp } = await import('firebase/firestore');
      await updateDoc(doc(db, 'chats', existingChat.id), {
        [`typing.${firebaseUser.uid}`]: serverTimestamp(),
      });
    } catch { /* best-effort */ }

    // Auto-clear after 5s of inactivity so the indicator vanishes when they stop.
    if (typingClearTimeout.current) clearTimeout(typingClearTimeout.current);
    typingClearTimeout.current = setTimeout(() => { lastTypingPing.current = 0; }, 5000);
  };

  const clearTypingNow = async () => {
    if (!existingChat?.id || !firebaseUser) return;
    lastTypingPing.current = 0;
    try {
      const db = await getDbSafe();
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'chats', existingChat.id), {
        [`typing.${firebaseUser.uid}`]: 0,
      });
    } catch { /* best-effort */ }
  };

  // Fetch friend's home_residence from their user doc
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const db = await getDbSafe();
        const userDoc = await getDoc(doc(db, 'users', String(friend.id)));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFriendLocation(data.showLocation === false ? 'Earth' : (data.home_residence || data.location || data.country || ''));
        }
      } catch (e) {
        console.warn('[DirectMessageScreen] Failed to fetch friend location:', e);
      }
    };
    fetchLocation();
  }, [friend.id]);

  const handleSendMessage = async () => {
    const trimmed = message.trim();
    if (!trimmed && !pendingImage) return;
    if (isSending || isUploadingImage) return;
    if (isInputLockedByRequest) return;

    setIsSending(true);
    setMessage('');
    clearTypingNow();

    // Upload image if one is pending
    let imageUrl: string | undefined;
    if (pendingImage && firebaseUser) {
      setIsUploadingImage(true);
      try {
        imageUrl = await StorageService.uploadChatImage(firebaseUser.uid, pendingImage);
      } catch (e) {
        toast.error('Image upload failed. Try again.');
        setIsSending(false);
        setIsUploadingImage(false);
        setMessage(trimmed);
        return;
      } finally {
        setIsUploadingImage(false);
      }
      clearPendingImage();
    }

    const now = new Date();
    // Snapshot the quote target before clearing it so the optimistic message
    // carries it AND we can drop the indicator above the input.
    const replySnapshot = replyingToMsg ? {
      id: replyingToMsg.id,
      text: replyingToMsg.text || (replyingToMsg.imageUrl ? '📷 Image' : (replyingToMsg.audioUrl ? '🎙️ Voice' : '')),
      senderId: replyingToMsg.sender === 'user' ? (firebaseUser?.uid || '') : String(friend.id),
      type: (replyingToMsg.imageUrl ? 'image' : replyingToMsg.audioUrl ? 'voice' : 'text') as 'text' | 'image' | 'voice',
    } : undefined;
    const newMessage: Message = {
      id: `${Date.now()}-${Math.random()}`,
      sender: 'user',
      text: trimmed,
      ...(imageUrl ? { imageUrl } : {}),
      ...(replySnapshot ? { replyTo: replySnapshot } : {}),
      time: now.toISOString(),
      timestamp: now.getTime(),
    };
    setReplyingToMsg(null);

    // If chat doesn't exist, it will be created by addMessage/enterChat
    if (!existingChat) {
      toast.success(`Chat started!`, {
        description: 'Your conversation will now appear in your chat list.',
      });
    }

    // 🔒 SECURITY: Handle friend/blocked validation errors
    try {
      await addMessage(friend.id, newMessage, isRequest);
      
      // 🔒 PATCH: If there were unread messages, clear them upon sending a new one
      if (existingChat && firebaseUser) {
        import('../services/api').then(({ MessagingService }) => {
          MessagingService.markMessagesAsRead(existingChat.id, firebaseUser.uid).catch(console.error);
        });
      }
    } catch (error: any) {
      console.error('[DirectMessageScreen] Error sending message:', error);
      // Restore the message text so the user can retry
      setMessage(trimmed);
      
      if (error.message?.includes('message_limit_reached')) {
        toast.error('Message limit reached', {
          description: 'You can send more messages once your friend request is accepted.'
        });
      } else if (error.message?.includes('not friends')) {
        toast.error('Cannot send message', {
          description: `You are no longer friends with ${friend.name}`
        });
        onBack();
      } else if (error.message?.includes('blocked_by_you')) {
        toast.error('Cannot send message', {
          description: 'You have blocked this user. Unblock them in Settings → Safety to message them.'
        });
        onBack();
      } else if (error.message?.includes('blocked')) {
        toast.error('Cannot send message', {
          description: 'You have been blocked by this user'
        });
        onBack();
      } else {
        toast.error('Failed to send message', {
          description: 'Please try again'
        });
      }
    } finally {
      // 🔒 ALWAYS unlock — even on error
      setIsSending(false);
    }
  };

  const handleUpdateMessage = async () => {
    if (!editingMessageId || message.trim() === '' || !existingChat || isSending) return;

    setIsSending(true);
    const trimmed = message.trim();
    try {
      await editMessage(existingChat.id, editingMessageId, trimmed);
      setMessage('');
      setEditingMessageId(null);
      toast.success('Message updated');
    } catch (error: any) {
      console.error('[DirectMessageScreen] Error updating message:', error);
      toast.error(error.message || 'Failed to update message');
    } finally {
      setIsSending(false);
    }
  };

  const startEditing = (msg: Message) => {
    setMessage(msg.text);
    setEditingMessageId(msg.id);
  };

  const cancelEditing = () => {
    setMessage('');
    setEditingMessageId(null);
  };

  const handleDeleteMessage = async (msgId: string) => {
    if (!existingChat) return;
    const confirmDelete = window.confirm("Are you sure you want to delete this message?");
    if (!confirmDelete) return;

    try {
      await deleteMessage(existingChat.id, msgId);
      toast.success("Message deleted");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete message");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingMessageId) {
        handleUpdateMessage();
      } else {
        handleSendMessage();
      }
    }
  };

  const handleSendVoice = async () => {
    if (!audioBlob || !firebaseUser) return;

    const loadingToast = toast.loading('Sending voice message...');
    try {
      // 1. Upload to storage
      const audioUrl = await StorageService.uploadVoiceReply(firebaseUser.uid, audioBlob);
      
      // 2. Add to chat
      const now = new Date();
      const newMessage: Message = {
        id: `${Date.now()}-${Math.random()}`,
        sender: 'user',
        text: '', // Empty text for pure voice message
        time: now.toISOString(),
        timestamp: now.getTime(),
        audioUrl,
        audioDuration: duration
      };

      await addMessage(friend.id, newMessage);
      resetRecorder();
      toast.dismiss(loadingToast);
    } catch (error) {
      console.error('[DirectMessageScreen] Voice send error:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send voice message');
    }
  };



  const handleViewProfile = () => {
    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: friend }));
  };

  const getDisplayTime = (msg: Message) => {
    if (toggledTimes.has(msg.id)) {
      return new Date(msg.time || msg.timestamp).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return formatTimeAgo(msg.time);
  };

  const handleToggleTime = (msgId: string) => {
    setToggledTimes(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const handleBlock = () => {
    onBack();
  };

  const handleReport = async () => {
    const reason = window.prompt(`Why are you reporting your conversation with ${friend.name}? (Harassment, Spam, etc.)`);
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error('A valid reason is required to report.');
      return;
    }
    try {
      const { reportToWatchers } = await import('../services/WatcherService');
      await reportToWatchers(
        String(firebaseUser?.uid || ''),
        String(friend.id),
        'dm',
        reason.trim(),
        {
          targetUserId: String(friend.id),
          reportedUsername: friend.username || friend.name,
          chatId: existingChat?.id || null,
        }
      );
      toast.success(`${friend.name} has been reported. Our team will review this.`);
    } catch (err) {
      toast.error('Failed to submit report.');
    }
  };

  // Navigate to a tribe or forum group by fetching the full document from Firestore
  const navigateToGroup = async (groupId: string, type: 'tribe' | 'forum' | 'room') => {
    try {
      if (type === 'room') {
        window.dispatchEvent(new CustomEvent('navigateToRoom', { detail: { id: groupId } }));
        return;
      }
      const db = await getDbSafe();
      // tribes live in 'tribes', forums in 'forums'
      const collection = type === 'tribe' ? 'tribes' : 'forums';
      const groupDoc = await getDoc(doc(db, collection, groupId));
      if (groupDoc.exists()) {
        const groupData = { id: groupDoc.id, ...groupDoc.data() };
        const eventName = type === 'tribe' ? 'navigateToTribeGroup' : 'navigateToHistoryForum';
        window.dispatchEvent(new CustomEvent(eventName, { detail: groupData }));
      } else {
        toast.error('Group not found');
      }
    } catch (err) {
      console.error('[DirectMessageScreen] navigateToGroup error:', err);
      toast.error('Could not open group');
    }
  };

  const avatarSrc = (friend as any).portfolio?.[(friend as any).profilePictureIndex] || friend.image || friend.avatar || '';

  const handleAllowRequest = async () => {
    if (!existingChat || isProcessingRequest) return;
    setIsProcessingRequest(true);
    try {
      await acceptRequest(existingChat.id);
    } catch (e) {
      toast.error('Failed to accept request');
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleDeclineRequest = async () => {
    if (!existingChat || isProcessingRequest) return;
    setIsProcessingRequest(true);
    try {
      await declineRequest(existingChat.id);
      onBack();
    } catch (e) {
      toast.error('Failed to decline request');
    } finally {
      setIsProcessingRequest(false);
    }
  };

  return (
    <div
      className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative overflow-hidden"
      style={{ height: '100dvh' }}
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      {/* HEADER – truly fixed at top, always visible */}
      <div
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212] backdrop-blur-lg"
      >
        <div
          className="flex items-center gap-4 px-4 pb-4"
          style={{ paddingTop: 'max(20px, calc(env(safe-area-inset-top) + 20px))' }}
        >
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2 rounded-full bg-black/10 dark:bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-black/20 dark:hover:bg-white/20 transition-colors border border-black/10 dark:border-white/10 shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-[#2C3E50] dark:text-[#E5E5E5]" />
          </motion.button>

          <div
            className="flex items-center gap-3 flex-1 cursor-pointer"
            onClick={handleViewProfile}
          >
            <div className="relative">
              <Avatar className="w-12 h-12 border-2 border-[#4478ea]">
                <AvatarImage src={avatarSrc} />
                <AvatarFallback>{(friend.name || 'U')[0]}</AvatarFallback>
              </Avatar>
              <ActivityStatus userId={friend.id} variant="dot" live />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-center gap-2">
                <h2 className="text-black dark:text-white truncate font-bold text-lg leading-tight uppercase" style={{ fontFamily: "'NarnialOne', sans-serif" }}>{friend.name}</h2>
                {(friend as any).rank && <RankBadge rank={(friend as any).rank} size={18} />}
                {((friend as any).countryFlag || friend.country) && (
                  <FlagImage
                    countryName={(friend as any).countryFlag || friend.country}
                    size="w80"
                    className="w-4 h-auto rounded-sm object-cover"
                  />
                )}
              </div>
              {friend.username && (
                <p className="text-black/50 dark:text-white/50 font-medium truncate" style={{ fontSize: '10px', lineHeight: '1' }}>@{friend.username.replace(/^@/, '')}</p>
              )}
              <ActivityStatus userId={friend.id} live style={{ fontSize: 10, marginTop: 3 }} />
            </div>
          </div>

          {/* Right side: Location & Action Menu */}
          <div className="flex items-center gap-2">
            {friendLocation && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/5 dark:bg-white/5 max-w-[120px] shrink-0">
                <MapPin className="w-3 h-3 text-[#F39C12] dark:text-[#4478ea] shrink-0" />
                <p className="font-semibold uppercase tracking-tight text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: '9px' }}>
                  {sanitizeLocation(friendLocation)}
                </p>
              </div>
            )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-2 h-auto rounded-full hover:bg-black/5 dark:hover:bg-white/5">
                <MoreVertical className="w-5 h-5 text-black dark:text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/10">
              <DropdownMenuItem onClick={handleViewProfile} className="cursor-pointer">
                <Info className="w-4 h-4 mr-2" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleReport}
                className="text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
              >
                🚨 Report User
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500 cursor-pointer"
                onClick={handleBlock}
              >
                <Ban className="w-4 h-4 mr-2" />
                Block User
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  if (existingChat && window.confirm("Delete this conversation? This cannot be undone.")) {
                    try {
                      await deleteChat(existingChat.id);
                      onBack();
                    } catch (e) {
                      console.error("Delete failed", e);
                    }
                  }
                }}
                className="text-[#E74C3C] hover:bg-[#E74C3C]/10 cursor-pointer"
              >
                <span className="w-4 h-4 mr-2 flex items-center justify-center">🗑️</span>
                Delete Conversation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </div>

      {/* REQUEST BANNERS */}
      {/* Sender banner: shown to person who sent the message request */}
      {isRequest && isSenderOfRequest && (
        <div className="fixed z-50 left-0 right-0 flex items-center justify-center gap-2 px-4 py-2 bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border-b border-[#F39C12]/20 dark:border-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea] text-xs font-medium"
          style={{ top: headerHeight }}
        >
          <Lock className="w-3.5 h-3.5 shrink-0" />
          <span>This is a message request. {isInputLockedByRequest ? `Waiting for ${friend.name} to accept.` : `You have 1 message to introduce yourself.`}</span>
        </div>
      )}
      {/* Recipient banner: Allow/Decline shown to person who received the request */}
      {isRequest && isRecipientOfRequest && (
        <div className="fixed z-50 left-0 right-0 flex items-center justify-between gap-3 px-4 py-2.5 bg-[#FAF8F5] dark:bg-[#1a1a1a] border-b border-black/10 dark:border-white/10 shadow-md"
          style={{ top: headerHeight }}
        >
          <p className="text-xs text-black/70 dark:text-white/70 font-medium flex-1">
            <span className="font-bold text-black dark:text-white">{friend.name}</span> wants to message you
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleDeclineRequest}
              disabled={isProcessingRequest}
              className="px-3 py-1.5 rounded-xl text-xs font-bold border border-black/10 dark:border-white/10 text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 transition-all active:scale-95"
            >
              Decline
            </button>
            <button
              onClick={handleAllowRequest}
              disabled={isProcessingRequest}
              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-sm active:scale-95 transition-all"
            >
              {isProcessingRequest ? '...' : 'Allow'}
            </button>
          </div>
        </div>
      )}

      {/* MESSAGES – fixed between header and input, starts at bottom, scroll up for history */}
      <div
        ref={scrollContainerRef}
        className="fixed left-0 right-0 overflow-y-auto z-30"
        style={{
          top: isRequest ? headerHeight + 40 : headerHeight,
          bottom: inputBarHeight,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Inner wrapper: min-h-full + justify-end pins messages to the bottom when there are few */}
        <div className="flex flex-col min-h-full justify-end py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center text-center px-6 min-h-[60vh]" />
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-6 min-h-[60vh]">
            <div className="w-20 h-20 mb-6 rounded-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center">
              <Avatar className="w-16 h-16 border-2 border-white">
                <AvatarImage src={avatarSrc} />
                <AvatarFallback>{(friend.name || 'U')[0]}</AvatarFallback>
              </Avatar>
            </div>
            <h3 className="text-2xl text-black dark:text-white mb-2">
              Start a conversation with {friend.name}
            </h3>
            <p className="text-[#666666] dark:text-[#E5E5E5] mb-4">
              Send a message to begin chatting.
            </p>
          </div>
        ) : (
          <div className="space-y-4 px-4">
            {messages.map((msg, index) => {
              const prevMsg = index > 0 ? messages[index - 1] : null;
              const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
              const isSameAsPrev = prevMsg?.sender === msg.sender;
              const isSameAsNext = nextMsg?.sender === msg.sender;

              // Clustering logic for margins
              const marginBottom = isSameAsNext ? 'mb-1' : 'mb-6';

              return (
                <div
                  key={msg.id}
                  className={`flex group ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} ${marginBottom}`}
                >
                {msg.sharedYap ? (
                  <div className={`max-w-[85%] ${msg.sender === 'user' ? 'self-end items-end' : 'self-start items-start'} flex flex-col gap-1`}>
                    {msg.sharedYap.originalYap ? (
                      <div
                        className={`w-full bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 overflow-hidden cursor-pointer hover:bg-[#E5E5E5] dark:hover:bg-[#2a2a2a] transition-colors
                          ${msg.sender === 'user' 
                            ? `rounded-2xl ${isSameAsNext ? 'rounded-br-sm' : ''} ${isSameAsPrev ? 'rounded-tr-sm' : ''}`
                            : `rounded-2xl ${isSameAsNext ? 'rounded-bl-sm' : ''} ${isSameAsPrev ? 'rounded-tl-sm' : ''}`
                          }
                        `}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('navigateToShoutDetail', {
                            detail: {
                              id: msg.sharedYap?.id,
                              author: msg.sharedYap?.author,
                              username: msg.sharedYap?.username,
                              avatar: msg.sharedYap?.avatar,
                              content: msg.sharedYap?.content,
                              time: msg.sharedYap?.time,
                              timestamp: msg.sharedYap?.time,
                              likes: msg.sharedYap?.likes || 0,
                              comments: msg.sharedYap?.comments || 0,
                              shouts: msg.sharedYap?.shouts || 0,
                              shoutCaption: msg.sharedYap?.content,
                              shoutImages: msg.sharedYap?.image ? [msg.sharedYap.image] : undefined,
                              originalYap: msg.sharedYap?.originalYap,
                            }
                          }));
                        }}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={msg.sharedYap.avatar} />
                              <AvatarFallback>{msg.sharedYap.author[0]}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm text-black dark:text-white truncate">{msg.sharedYap.author}</p>
                                <p className="text-xs text-[#666666] dark:text-[#999999] truncate">{msg.sharedYap.username}</p>
                                <p className="text-xs text-[#999999] dark:text-[#808080] shrink-0">· {msg.sharedYap.time}</p>
                              </div>
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#16A34A]">
                                  <Megaphone className="w-4 h-4" />
                                  <span className="text-sm">Shouted</span>
                                </span>
                              </div>
                              {msg.sharedYap.content && (
                                <p className="text-sm text-black dark:text-[#E5E5E5] mt-2 wrap-break-word">
                                  {msg.sharedYap.content}
                                </p>
                              )}
                            </div>
                          </div>
                          {msg.sharedYap.image && (
                            <div className="mb-2 rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-fit mx-auto">
                              <img 
                                src={msg.sharedYap.image} 
                                alt="Shout" 
                                className="object-contain"
                                style={{ maxHeight: '180px', maxWidth: '100%', width: 'auto', height: 'auto' }}
                              />
                            </div>
                          )}
                          <EmbeddedYap
                            small={true}
                            yapId={getOriginalYapId(msg.sharedYap.originalYap)}
                            username={msg.sharedYap.originalYap.username || ''}
                            authorName={msg.sharedYap.originalYap.author || 'Unknown'}
                            avatar={''}
                            timestamp={''}
                            content={msg.sharedYap.originalYap.content || ''}
                            poll={(msg.sharedYap.originalYap as any)?.poll}
                            image={undefined}
                            likes={0}
                            comments={0}
                            shouts={0}
                            isDeleted={(msg.sharedYap.originalYap as any)?.deleted}
                            minimal={!msg.sharedYap.content}
                            onYapClick={(e) => {
                              e?.stopPropagation();
                              window.dispatchEvent(new CustomEvent('navigateToYapDetail', {
                                detail: {
                                  id: getOriginalYapId(msg.sharedYap.originalYap),
                                  author: msg.sharedYap.originalYap.author,
                                  username: msg.sharedYap.originalYap.username,
                                  content: msg.sharedYap.originalYap.content,
                                  avatar: '', // Fallback
                                  time: '', // Fallback
                                  likes: 0,
                                  comments: 0,
                                  shouts: 0,
                                  visibility: 'public'
                                }
                              }));
                            }}
                            maxLines={2}
                            showActions={false}
                          />
                        </div>
                      </div>
                    ) : (
                      <EmbeddedYap
                        yapId={getYapInteractionId(msg.sharedYap)}
                        username={msg.sharedYap.username || ''}
                        authorName={msg.sharedYap.author || 'Unknown'}
                        avatar={msg.sharedYap.avatar || ''}
                        timestamp={msg.sharedYap.time || ''}
                        content={msg.sharedYap.content || ''}
                        poll={(msg.sharedYap as any)?.poll}
                        image={msg.sharedYap.image}
                        likes={msg.sharedYap.likes || 0}
                        comments={msg.sharedYap.comments || 0}
                        shouts={msg.sharedYap.shouts || 0}
                        isDeleted={(msg.sharedYap as any)?.deleted}
                        onYapClick={() => {
                          window.dispatchEvent(new CustomEvent('navigateToYapDetail', {
                            detail: {
                              id: getYapInteractionId(msg.sharedYap),
                              author: msg.sharedYap.author,
                              username: msg.sharedYap.username,
                              avatar: msg.sharedYap.avatar,
                              content: msg.sharedYap.content,
                              time: msg.sharedYap.time,
                              likes: msg.sharedYap.likes || 0,
                              comments: msg.sharedYap.comments || 0,
                              shouts: msg.sharedYap.shouts || 0,
                              image: msg.sharedYap.image,
                              visibility: 'public'
                            }
                          }));
                        }}
                        maxLines={3}
                        showActions={false}
                        small={true}
                      />
                    )}

                    {/* Integrated Comment (Fixing "floating boxes") */}
                    {msg.text && msg.text !== `Shared a yap from ${msg.sharedYap.author}` && msg.text !== `Shared a shout from ${msg.sharedYap.author}` && msg.text !== 'Shared a shout' && (
                       <div className={`px-2 py-1 ${msg.sender === 'user' ? 'text-right' : 'text-left'}`}>
                          <p className={`text-sm ${msg.sender === 'user' ? 'text-white' : 'text-black dark:text-white font-medium'}`}>
                             {msg.text}
                          </p>
                       </div>
                    )}
                    <p 
                      onClick={() => handleToggleTime(msg.id)}
                      className={`text-[10px] mt-0.5 cursor-pointer transition-opacity ${msg.sender === 'user' ? 'text-right text-[#999999] opacity-70 hover:opacity-100' : 'text-left text-[#666666] dark:text-[#999999] hover:opacity-80'}`}
                    >
                      {getDisplayTime(msg)}
                    </p>
                  </div>
                ) : msg.groupInvite ? (
                  <div className={`max-w-[85%] w-[260px] ${msg.sender === 'user' ? 'self-end items-end' : 'self-start items-start'} flex flex-col gap-2`}>
                    <div className={`w-full rounded-2xl bg-white dark:bg-[#1a1a1a] shadow-lg border border-black/8 dark:border-white/10 overflow-hidden ${msg.sender === 'user' ? 'self-end' : 'self-start'}`}>
                      {/* Banner Image — clickable to preview group */}
                      <div
                        className="h-32 w-full relative overflow-hidden cursor-pointer"
                        onClick={() => navigateToGroup(msg.groupInvite!.id, msg.groupInvite!.type)}
                      >
                        <img
                          src={msg.groupInvite.bannerUrl || 'https://images.unsplash.com/photo-1511632765486-a01980e01a18'}
                          className="w-full h-full object-cover"
                          alt="Group Banner"
                        />
                        <div className="absolute inset-0 bg-linear-to-b from-transparent to-black/60" />
                        <div className="absolute top-2 left-2">
                          <span className="text-[10px] uppercase tracking-widest font-bold bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full">
                            {msg.groupInvite.type} Invite
                          </span>
                        </div>
                        {/* Tap to preview hint */}
                        <div className="absolute bottom-2 right-2">
                          <span className="text-[10px] text-white/70 bg-black/30 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                            Tap to preview →
                          </span>
                        </div>
                      </div>
                      {/* Group Info — also clickable */}
                      <div
                        className="px-3 pt-3 pb-1 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        onClick={() => navigateToGroup(msg.groupInvite!.id, msg.groupInvite!.type)}
                      >
                        <p className="text-black dark:text-white font-bold text-base leading-snug">{msg.groupInvite.name}</p>
                        <p className="text-[#888] dark:text-[#aaa] text-xs capitalize mt-0.5">{msg.groupInvite.type}</p>
                      </div>
                      {/* Accept / Deny row */}
                      {msg.sender !== 'user' && (
                        <div className="px-3 pb-3 pt-2 flex gap-2">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // A room is never auto-joined: every seat goes
                                // through a request the host or a mod approves,
                                // so the invite opens the room instead.
                                //
                                // Cross-bracket invites are refused at send time;
                                // this covers one sent before that existed. The
                                // room's own read rule is the real enforcement.
                                if (msg.groupInvite?.type === 'room') {
                                  window.dispatchEvent(new CustomEvent('navigateToRoom', {
                                    detail: { id: msg.groupInvite.id },
                                  }));
                                  return;
                                }

                                const isPrivateGroup = msg.groupInvite?.isPublic === false;
                                if (isPrivateGroup) {
                                  // Private (invite-only) group: use acceptGroupInvite which
                                  // bypasses the public-gate — the invite IS the authorization.
                                  await acceptGroupInvite(
                                    msg.groupInvite!.type,
                                    msg.groupInvite!.id
                                  );
                                } else if (msg.groupInvite?.type === 'tribe') {
                                  await joinTribe(msg.groupInvite.id);
                                } else if (msg.groupInvite?.type === 'forum') {
                                  await joinForum(msg.groupInvite.id);
                                }
                                toast.success(`Joined ${msg.groupInvite?.name}!`);
                                // Navigate to the group after joining
                                await navigateToGroup(msg.groupInvite!.id, msg.groupInvite!.type);
                              } catch (err: any) {
                                toast.error(err?.message || 'Failed to join group');
                              }
                            }}
                            className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white text-sm font-bold rounded-xl transition-all"
                          >
                            ✓ Accept
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toast('Invite declined');
                            }}
                            className="flex-1 py-2.5 bg-black/8 dark:bg-white/8 hover:bg-black/15 dark:hover:bg-white/15 active:scale-[0.98] text-black dark:text-white text-sm font-semibold rounded-xl transition-all"
                          >
                            ✕ Deny
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Text is now handled inside the container above to avoid floating boxes */}
                    <p 
                      onClick={() => handleToggleTime(msg.id)}
                      className={`text-xs cursor-pointer transition-opacity hover:opacity-80 ${msg.sender === 'user' ? 'text-right text-[#999999] dark:text-[#808080]' : 'text-left text-[#999999] dark:text-[#808080]'}`}
                    >
                      {getDisplayTime(msg)}
                    </p>
                  </div>
                ) : (
                    <div className={`flex flex-col gap-1 max-w-[85%] ${msg.sender === 'user' ? 'self-end items-end' : 'self-start items-start'}`}>
                      {/* Row: [⋯ dots] [bubble] — dots sit outside and to the left of the bubble */}
                      <div className="flex flex-row items-center gap-1.5">

                        {/* ⋯ dots — LEFT of bubble, flex sibling, not absolute */}
                        {msg.sender === 'user' && !msg.audioUrl && !msg.sharedYap && !msg.groupInvite && (
                          <DropdownMenu
                            open={activeMessageMenu === msg.id}
                            onOpenChange={(open) => setActiveMessageMenu(open ? msg.id : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-7 w-7 p-0 shrink-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10 opacity-40 hover:opacity-100 active:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); }}
                              >
                                <MoreVertical className="h-4 w-4 text-[#666666] dark:text-[#999999]" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" side="bottom" className="bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 min-w-[160px]">
                              <DropdownMenuItem
                                onClick={() => { setReplyingToMsg(msg); setActiveMessageMenu(null); }}
                                className="cursor-pointer"
                              >
                                ↩ Reply
                              </DropdownMenuItem>
                              {msg.text && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    navigator.clipboard.writeText(msg.text);
                                    toast.success('Copied');
                                    setActiveMessageMenu(null);
                                  }}
                                  className="cursor-pointer"
                                >
                                  📋 Copy Text
                                </DropdownMenuItem>
                              )}
                              {(Date.now() - msg.timestamp < 15 * 60 * 1000) && (
                                <DropdownMenuItem
                                  onClick={() => { startEditing(msg); setActiveMessageMenu(null); }}
                                  className="cursor-pointer"
                                >
                                  ✏️ Edit Message
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => { handleDeleteMessage(msg.id); setActiveMessageMenu(null); }}
                                className="cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-50/10"
                              >
                                🗑️ Delete for you
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {/* The bubble — double-tap to like, long-press to quote-reply */}
                        <div
                          onPointerDown={() => handleMessagePointerDown(msg)}
                          onPointerUp={() => handleMessagePointerUp(msg)}
                          onPointerCancel={handleMessagePointerCancel}
                          onPointerLeave={handleMessagePointerCancel}
                          style={{ touchAction: 'manipulation', userSelect: 'none', position: 'relative' }}
                          className={`px-4 pt-3 pb-3 ${msg.sender === 'user'
                            ? `bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white ${isSameAsNext ? 'rounded-2xl rounded-br-sm' : 'rounded-2xl'} ${isSameAsPrev ? 'rounded-tr-sm' : ''}`
                            : `bg-[#F5F5F5] dark:bg-[#2a2a2a] text-black dark:text-white ${isSameAsNext ? 'rounded-2xl rounded-bl-sm' : 'rounded-2xl'} ${isSameAsPrev ? 'rounded-tl-sm' : ''}`
                          }`}
                        >
                          {/* Quoted reply preview — appears at the top of the bubble */}
                          {msg.replyTo && (
                            <div
                              className={`mb-2 px-2 py-1.5 rounded-lg text-[11px] leading-snug ${msg.sender === 'user' ? 'bg-white/15 text-white/90' : 'bg-black/8 dark:bg-white/10 text-black/70 dark:text-white/70'}`}
                              style={{ borderLeft: '2px solid currentColor', maxWidth: 260 }}
                            >
                              <p className="font-semibold opacity-70 mb-0.5">
                                {String(msg.replyTo.senderId) === String(firebaseUser?.uid) ? 'You' : friend.name}
                              </p>
                              <p className="truncate" style={{ maxWidth: 240 }}>{msg.replyTo.text || ''}</p>
                            </div>
                          )}
                          {msg.imageUrl && (
                            <div className={`${msg.text ? 'mb-2' : ''} -mx-4 -mt-3 overflow-hidden rounded-t-2xl`}>
                              {/* Same viewer yaps and portfolio pictures use, so a
                                  chat image opens full-size, zooms and swipes the
                                  way an image does everywhere else in the app
                                  rather than being the one kind you cannot open. */}
                              <img
                                src={msg.imageUrl}
                                alt=""
                                className="w-full max-w-[260px] h-auto object-cover block cursor-pointer"
                                style={{ maxHeight: 300 }}
                                draggable={false}
                                onClick={(e) => { e.stopPropagation(); openImage([String(msg.imageUrl)], 0); }}
                              />
                            </div>
                          )}
                          {msg.text && <p className="wrap-break-word whitespace-pre-wrap text-[15px] leading-relaxed">{msg.text}</p>}
                          {msg.audioUrl && (
                            <div className="mt-2 min-w-[200px]">
                              <VoiceReplyBubble audioUrl={msg.audioUrl} duration={msg.audioDuration} />
                            </div>
                          )}
                          <div className={`flex items-center mt-1 ${msg.sender === 'user' ? 'justify-between gap-3' : 'justify-start gap-1.5'}`}>
                            {/* Sent / Seen receipt — pinned LEFT, same size/weight as the time on the right. */}
                            {msg.sender === 'user' && !msg.isProvisional ? (
                              <span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.80)' }}>
                                {msg.read ? 'Seen' : 'Sent'}
                              </span>
                            ) : null}
                            <div className="flex items-center gap-1.5">
                              {msg.edited && <span className={`text-[10px] ${msg.sender === 'user' ? 'text-white/60' : 'text-[#999999]'}`}>(edited)</span>}
                              <p
                                onClick={() => handleToggleTime(msg.id)}
                                className={`text-xs cursor-pointer transition-opacity ${msg.sender === 'user' ? 'text-white/80 hover:text-white' : 'text-[#666666] dark:text-[#999999] hover:opacity-80'}`}
                              >
                                {getDisplayTime(msg)}
                              </p>
                            </div>
                          </div>
                          {/* ❤️ Liked badge — overlay at the bottom corner of the bubble. */}
                          {isMessageLiked(msg) && (
                            <div
                              aria-label="Liked"
                              style={{
                                position: 'absolute',
                                bottom: -8,
                                [msg.sender === 'user' ? 'right' : 'left']: 10,
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: '#fff',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                pointerEvents: 'none',
                              } as any}
                            >
                              <span style={{ color: '#E74C3C' }}>❤</span>
                            </div>
                          )}
                        </div>
                        {/* Friend-message menu: Reply + Copy on the RIGHT of the bubble. */}
                        {msg.sender === 'friend' && !msg.sharedYap && !msg.groupInvite && (
                          <DropdownMenu
                            open={activeMessageMenu === msg.id}
                            onOpenChange={(open) => setActiveMessageMenu(open ? msg.id : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-7 w-7 p-0 shrink-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10 opacity-40 hover:opacity-100 active:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); }}
                              >
                                <MoreVertical className="h-4 w-4 text-[#666666] dark:text-[#999999]" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" side="bottom" className="bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 min-w-[160px]">
                              <DropdownMenuItem
                                onClick={() => { setReplyingToMsg(msg); setActiveMessageMenu(null); }}
                                className="cursor-pointer"
                              >
                                ↩ Reply
                              </DropdownMenuItem>
                              {msg.text && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    navigator.clipboard.writeText(msg.text);
                                    toast.success('Copied');
                                    setActiveMessageMenu(null);
                                  }}
                                  className="cursor-pointer"
                                >
                                  📋 Copy Text
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                )}
              </div>
            );
          })}
            {friendTyping && (
              <div className="flex self-start items-center mt-1 px-4 py-2 rounded-2xl bg-[#F5F5F5] dark:bg-[#2a2a2a]" style={{ width: 'fit-content' }}>
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
                <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
                <style>{`
                  .typing-dot {
                    display: inline-block;
                    width: 6px; height: 6px; margin: 0 2px;
                    border-radius: 50%;
                    background: #888;
                    animation: typing-bounce 1s infinite ease-in-out;
                  }
                  @keyframes typing-bounce {
                    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                    30%           { transform: translateY(-4px); opacity: 1; }
                  }
                `}</style>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
        </div> {/* end min-h-full justify-end wrapper */}
      </div>

      {/* hidden file input */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImagePicked}
      />

      {/* INPUT BAR – truly fixed at bottom */}
      <div
        ref={inputBarRef}
        className="fixed bottom-0 left-0 right-0 z-50 px-4 py-4 border-t border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212] backdrop-blur-lg"
        style={{ paddingBottom: 'max(24px, calc(env(safe-area-inset-bottom) + 12px))' }}
      >
        {/* Replying-to indicator */}
        {replyingToMsg && (
          <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-black/5 dark:bg-white/8 border-l-2 border-[#4478ea]">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-[#4478ea] uppercase tracking-wide">
                Replying to {replyingToMsg.sender === 'user' ? 'yourself' : friend.name}
              </p>
              <p className="text-xs truncate text-black/70 dark:text-white/70">
                {replyingToMsg.text || (replyingToMsg.imageUrl ? '📷 Image' : (replyingToMsg.audioUrl ? '🎙️ Voice message' : ''))}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setReplyingToMsg(null)}
              className="text-[#999999] hover:text-black dark:hover:text-white shrink-0"
              aria-label="Cancel reply"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Image preview strip */}
        {pendingImagePreview && (
          <div className="mb-2 flex items-start gap-2">
            <div className="shrink-0" style={{ position: 'relative', width: 80, height: 80 }}>
              <img
                src={pendingImagePreview}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}
              />
              <button
                type="button"
                onClick={clearPendingImage}
                aria-label="Remove image"
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.75)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #fff',
                  cursor: 'pointer',
                  padding: 0,
                  zIndex: 2,
                }}
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
            {isUploadingImage && <p className="text-xs text-[#999999] self-end mb-1">Uploading…</p>}
          </div>
        )}
        <div className="flex items-end gap-2">
          {!isRecording && !audioBlob ? (
            <div
              // minHeight inline: min-h-[48px] is not in the committed
              // pre-compiled index.css, so the bar's resting height never
              // applied at all.
              style={{ minHeight: 48 }}
              className={`flex-1 flex items-end bg-white/80 dark:bg-[#1a1a1a] border rounded-2xl px-4 py-2 transition-colors ${isInputLockedByRequest ? 'border-[#F39C12]/30 dark:border-[#4478ea]/30 opacity-60' : 'border-black/10 dark:border-white/10'}`}
            >
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => { setMessage(e.target.value); if (e.target.value.length > 0) pingTyping(); }}
                onKeyDown={handleKeyPress}
                placeholder={isInputLockedByRequest ? `Waiting for ${friend.name} to accept your request...` : (editingMessageId ? "Edit your message..." : "Type a message...")}
                rows={1}
                className="flex-1 bg-transparent border-none focus:outline-none resize-none px-0 text-base leading-6 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#666666] scrollbar-hide"
                disabled={isUserBlocked(String(friend.id)) || !!isInputLockedByRequest}
                // Height is owned ENTIRELY by the auto-resize effect above.
                // It previously also carried h-[24px] and an inline height of
                // 24px, so three declarations fought over one property and the
                // box stayed one line high no matter how much was typed.
                // minHeight keeps a single line before any JS runs; overflowY
                // gives a scrollbar once growth hits the 160px ceiling instead
                // of hiding the text past it.
                style={{ minHeight: 24, maxHeight: 160, overflowY: 'auto' }}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center gap-2">
              {audioBlob && !isRecording && (
                <div className="flex-1">
                  <VoiceReplyBubble 
                    audioUrl={URL.createObjectURL(audioBlob)} 
                    duration={duration} 
                    onRemove={() => resetRecorder()} 
                  />
                </div>
              )}
              {isRecording && (
                <div className="flex-1">
                  <VoiceRecorderUI 
                    isRecording={isRecording} 
                    isPaused={isPaused} 
                    duration={duration} 
                    maxDuration={60000} 
                    onStart={startRecording} 
                    onStop={stopRecording} 
                    onCancel={resetRecorder}
                    onPause={pauseRecording}
                    onResume={resumeRecording}
                  />
                </div>
              )}
            </div>
          )}

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-1.5 shrink-0">
            {!isRecording && !audioBlob && (
              <>
                <Button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isUserBlocked(String(friend.id)) || !!isInputLockedByRequest || isUploadingImage}
                  className="rounded-full w-10 h-10 p-0 bg-gray-200 dark:bg-[#2a2a2a] text-[#666666] dark:text-white shadow-sm active:scale-95 transition-all"
                >
                  <ImageIcon className="w-5 h-5" />
                </Button>
                <Button
                  onClick={startRecording}
                  disabled={isUserBlocked(String(friend.id)) || !!isInputLockedByRequest}
                  className="rounded-full w-10 h-10 p-0 bg-brand-gradient text-white shadow-lg active:scale-95 transition-all"
                >
                  <Mic className="w-5 h-5" />
                </Button>
                <Button
                  onClick={editingMessageId ? handleUpdateMessage : handleSendMessage}
                  disabled={isUserBlocked(String(friend.id)) || (!message.trim() && !pendingImage) || isSending || isUploadingImage}
                  className={`rounded-full w-10 h-10 p-0 transition-all ${
                    (message.trim() || pendingImage) && !isSending && !isUploadingImage
                      ? 'bg-brand-gradient text-white shadow-lg active:scale-95'
                      : 'bg-gray-200 dark:bg-gray-800 text-gray-400 opacity-50'
                  }`}
                >
                  <Send className="w-5 h-5 ml-0.5" />
                </Button>
              </>
            )}

            {isRecording && (
              <Button
                onClick={stopRecording}
                className="rounded-full w-12 h-12 p-0 bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 active:scale-95 transition-all"
              >
                <Square className="w-5 h-5 fill-current" />
              </Button>
            )}

            {audioBlob && !isRecording && (
              <Button
                onClick={handleSendVoice}
                className="rounded-full w-12 h-12 p-0 bg-brand-gradient text-white shadow-lg hover:scale-105 active:scale-95"
              >
                <Send className="w-5 h-5 ml-0.5" />
              </Button>
            )}
          </div>
        </div>
        {isUserBlocked(String(friend.id)) && (
          <p className="text-xs text-[#E74C3C] mt-2 text-center font-medium">
            🔒 You have blocked this user.
          </p>
        )}
        {!existingChat && messages.length === 0 && (
          <p className="text-xs text-[#999999] dark:text-[#808080] mt-2 text-center">
            💡 This chat will be saved to your messages after you send your first message
          </p>
        )}
      </div>
    </div>
  );
}
